import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Type } from 'class-transformer';
import { ClassTransformerHelper } from '../../../../../helpers/class.transformer.helper';
import { WorkflowType } from '../../../../../models/enums/workflowtype.enum';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../../services/workflow.service';
import { DataCheck, WorkflowDialogContent } from '../../../../../workflow/workflow.dialog';
import { RegionSetting } from '../region.control';

@Component({
    selector: 'wf-single-feature-model-settings',
    templateUrl: './single.feature.model.settings.html',
    styleUrls: ['./single.feature.model.settings.css']
})
export class SingleFeatureModelSettings extends WorkflowDialogContent {
    Data = new SingleFeatureModelSettingsData();
    Columns = [];
    HasDateCol = false;
    SubGroupingDisabled = true;

    static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'singleFeatureModelWFModule';
        reg.Caption = '@@Single feature model train';
        reg.GroupID = 'aioperations';
        reg.Index = 200;
        reg.SettingsControl = SingleFeatureModelSettings;
        reg.SettingsTypeHelper = new SingleFeatureModelSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    constructor(private translate: TranslateService) {
        super();
        this.UseActualState = true;
        this.HasExpressions = true;
    }

    initialize(data: any) {
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, SingleFeatureModelSettingsData);
        }
        const dataSource = this.ActualState.get('DataSource');
        if (dataSource && dataSource.Columns) {
            const list = [];
            let groupCol, subGroupCol;
            dataSource.Columns.forEach(col => {
                const val = {
                    Name: col.Name,
                    DateCol: col.Name === this.Data.DateColumn,
                    ValCol: col.Name === this.Data.ValueColumn,
                    CatCol: this.Data.CatColumns && this.Data.CatColumns.some(cc => cc === col.Name),
                    Grouping: false,
                    SubGrouping: false
                };
                if (this.Data.Grouping && col.Name === this.Data.Grouping) {
                    groupCol = val;
                }
                if (this.Data.SubGrouping && col.Name === this.Data.SubGrouping) {
                    subGroupCol = val;
                }
                if (val.DateCol) {
                    this.HasDateCol = true;
                }
                list.push(val);
            });
            if (groupCol && groupCol.CatCol) {
                groupCol.Grouping = true;
                this.SubGroupingDisabled = false;
                if (subGroupCol && subGroupCol.CatCol && !subGroupCol.Grouping) {
                    subGroupCol.SubGrouping = true;
                }
            }
            this.Columns = list;
        }
    }

    getResult() {
        let dateCol, valCol, groupCol, subGroupCol;
        const catCols = [];
        this.Columns.forEach(c => {
            if (c.DateCol) {
                dateCol = c.Name;
            }
            if (c.ValCol) {
                valCol = c.Name;
            }
            if (c.CatCol) {
                catCols.push(c.Name);
                if (c.Grouping) {
                    groupCol = c.Name;
                } else if (c.SubGrouping) {
                    subGroupCol = c.Name;
                }
            }
        });
        this.Data.DateColumn = dateCol;
        this.Data.ValueColumn = valCol;
        this.Data.CatColumns = catCols;
        this.Data.Grouping = groupCol;
        if (groupCol && subGroupCol) {
            this.Data.SubGrouping = subGroupCol;
        } else {
            this.Data.SubGrouping = null;
        }
        if (!this.Data.DateColumn) {
            this.Data.Augments = 0;
            this.Data.Rounding = null;
        }
        return this.Data;
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (this.Columns.length > 0) {
            let hasDateCol = false;
            let hasValCol = false;
            this.Columns.some(x => {
                if (x.DateCol) {
                    hasDateCol = true;
                }
                if (x.ValCol) {
                    hasValCol = true;
                }
                return hasDateCol && hasValCol;
            });
            if (!hasDateCol) {
                retVal.IsCorrect = false;
                if (hasValCol) {
                    retVal.Error = '@@Bitte waehlen Sie eine Datumsspalte';
                } else {
                    retVal.Error = '@@Bitte waehlen Sie eine Datumsspalte und eine Wertspalte';
                }
            } else if (!hasValCol) {
                retVal.IsCorrect = false;
                retVal.Error = '@@Bitte waehlen Sie eine Wertspalte';
            }
        }
        return retVal;
    }

    dateColChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.DateCol = false);
            col.DateCol = true;
            this.HasDateCol = true;
        } else {
            this.HasDateCol = false;
        }
    }

    valColChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.ValCol = false);
            col.ValCol = true;
        }
    }

    catColChanged(ev, col) {
        if (!ev || !ev.checked) {
            if (col.Grouping) {
                col.Grouping = false;
                this.Columns.forEach(c => c.SubGrouping = false);
                this.SubGroupingDisabled = true;
            }
            if (col.SubGrouping) {
                col.SubGrouping = false;
            }
        }
    }

    groupingChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.Grouping = false);
            col.Grouping = true;
            col.SubGrouping = false;
            this.SubGroupingDisabled = false;
        } else {
            this.Columns.forEach(c => c.SubGrouping = false);
            this.SubGroupingDisabled = true;
        }
    }

    subGroupingChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.SubGrouping = false);
            col.SubGrouping = true;
        }
    }

    estimatorsChanged(ev) {
        if (ev && ev.value) {
            this.Data.ModelEstimators = ev.value;
        }
    }

    depthChanged(ev) {
        if (ev && ev.value) {
            this.Data.ModelDepth = ev.value;
        }
    }

    GetExpressionProperties() {
        return [
            {
                Caption: this.translate.instant('@@Datumsspalte'),
                Value: 'DateColumn'
            },
            {
                Caption: this.translate.instant('@@Wertspalte'),
                Value: 'ValueColumn'
            },
            {
                Caption: this.translate.instant('@@Datums-Ebene'),
                Value: 'Rounding'
            },
            {
                Caption: this.translate.instant('@@Zusaetzliche Spalten'),
                Value: 'Augments'
            },
            {
                Caption: this.translate.instant('@@Datenzeitraum in Tagen'),
                Value: 'Days'
            },
            {
                Caption: this.translate.instant('@@Vorhersagezeitraum in Tagen'),
                Value: 'DaysOut'
            },
            {
                Caption: this.translate.instant('@@Kategorie'),
                Value: 'CatColumns'
            },
            {
                Caption: this.translate.instant('@@Gruppierung'),
                Value: 'Grouping'
            },
            {
                Caption: this.translate.instant('@@Untergruppierung'),
                Value: 'SubGrouping'
            },
            {
                Caption: this.translate.instant('@@ModelEstimators'),
                Value: 'ModelEstimators'
            },
            {
                Caption: this.translate.instant('@@ModelDepth'),
                Value: 'ModelDepth'
            }
        ];
    }
}

// @dynamic
export class SingleFeatureModelSettingsData {
    DateColumn: string;
    ValueColumn: string;
    Rounding: string;
    Augments: number;
    Days = 2;
    DaysOut = 1;
    CatColumns = [];
    Grouping: string;
    SubGrouping: string;
    ModelEstimators = 30;
    ModelDepth = 20;
    Aggregation = 0;
    @Type(() => RegionSetting)
    RegionSetting = new RegionSetting();
}

export class SingleFeatureModelSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        const retVal = new WorkflowExitInfo();
        retVal.Type = 'objData';
        return [retVal];
    }
    getEntryPoints(): WorkflowExitInfo[] {
        const def = new WorkflowExitInfo();
        def.Type = 'relData';
        const fe = new WorkflowExitInfo();
        fe.ID = 1;
        fe.Label = '@@ForEach';
        fe.Type = 'forEach';
        return [def, fe];
    }
    getEmptySettingsInstance() {
        return new SingleFeatureModelSettingsData();
    }
}
